import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowForward, ArrowBack, CheckCircleOutline } from '@mui/icons-material';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

const JambSubjectSelection: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const initialJambSubjects = user?.jambSubjects;
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(() => (
    Array.isArray(initialJambSubjects) && initialJambSubjects.length === 4
      ? ['Use of English', ...initialJambSubjects.filter((subject) => subject !== 'Use of English')]
      : ['Use of English']
  ));
  const [subjects, setSubjects] = useState<string[]>(initialJambSubjects || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mockTestId, setMockTestId] = useState('');
  const initializedRef = React.useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!user) {
      navigate('/high-school/login');
      return;
    }
    initializedRef.current = true;

    // Initialize mock test
    const initializeMockTest = async () => {
      try {
        const response = await api.post('/mock-test/initialize', {
          examType: 'JAMB',
        });
        setMockTestId(response.data.mockTestId);

        // The registration combination is authoritative. Legacy users without
        // a registration combo may still use their saved mock-test combination.
        if ((!Array.isArray(user.jambSubjects) || user.jambSubjects.length !== 4) &&
            Array.isArray(response.data.lastSubjectCombination) && response.data.lastSubjectCombination.length === 4) {
          setSelectedSubjects(response.data.lastSubjectCombination);
          setSubjects(response.data.lastSubjectCombination);
          if (response.data.lastSubjectCombinationChangedDate) {
            const lastChange = new Date(response.data.lastSubjectCombinationChangedDate);
            const now = new Date();
            const monthsDiff = (now.getFullYear() - lastChange.getFullYear()) * 12 + (now.getMonth() - lastChange.getMonth());
            if (monthsDiff < 8) {
              setError('Your saved subject combination is locked until ' + new Date(lastChange.setMonth(lastChange.getMonth() + 8)).toLocaleDateString());
            }
          }
        }
      } catch (error: any) {
        if (error.response?.status === 429) {
          setError(`${error.response.data.message}. Next attempt available at: ${new Date(error.response.data.nextAttemptDate).toLocaleString()}`);
        } else {
          setError('Error initializing mock test. Please try again.');
        }
      }
    };

    initializeMockTest();

  }, [user, navigate]);

  // Persist current selection to sessionStorage to ensure confirmation/instruction pages can show it immediately
  useEffect(() => {
    if (mockTestId && Array.isArray(selectedSubjects)) {
      try {
        sessionStorage.setItem(`selectedSubjects_${mockTestId}`, JSON.stringify(selectedSubjects));
      } catch (e) {
        // ignore storage errors
      }
    }
  }, [selectedSubjects, mockTestId]);

  const handleNext = async () => {
    if (selectedSubjects.length !== 4) {
      setError('Please select exactly 4 subjects (English + 3 others)');
      return;
    }
    setError(''); // Clear error before proceeding

    // Always attempt to persist subjects to backend (backend will check if it's a true change)
    setLoading(true);
    try {
      await api.put(`/mock-test/${mockTestId}/subjects`, {
        subjects: selectedSubjects,
      });
      if (user) updateUser({ ...user, jambSubjects: selectedSubjects });
      navigate(`/mock-test/jamb/confirm/${mockTestId}`);
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || 'Error updating subjects';
      setError(errorMsg);
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('[JambSubjectSelection] Error saving subjects:', errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: theme.palette.background.default, py: { xs: 4, md: 8 } }}>
      <Container maxWidth="md">
        <Box sx={{ mb: 6 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/')}
            sx={{ mb: 3, textTransform: 'none', fontWeight: 600 }}
          >
            Back to Home
          </Button>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: 'primary.dark' }}>
            JAMB Mock Test - Subject Selection
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            Your JAMB subjects were selected during registration and are shown here for confirmation.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Card sx={{ mb: 4 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
              Your Registered Subjects
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 4 }}>
              {subjects.map((subject) => (
                <Box
                  key={subject}
                  sx={{
                    p: 2.5,
                    border: '2px solid',
                    borderColor: selectedSubjects.includes(subject) ? 'primary.main' : 'divider',
                    borderRadius: 1.5,
                    cursor: 'default',
                    background: selectedSubjects.includes(subject)
                      ? alpha(theme.palette.primary.main, 0.1)
                      : 'transparent',
                    transition: 'all 0.3s ease',
                    opacity: subject === 'Use of English' ? 1 : selectedSubjects.includes(subject) ? 1 : 0.7,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CheckCircleOutline sx={{ color: 'success.main' }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {subject}
                      </Typography>
                      {subject === 'Use of English' && (
                        <Typography variant="caption" color="text.secondary">
                          Compulsory
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{ mb: 4, p: 2, background: alpha(theme.palette.info.main, 0.1), borderRadius: 1.5, border: `1px solid ${alpha(theme.palette.info.main, 0.3)}` }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>JAMB Subject Combination</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {selectedSubjects.map((subject) => (
                    <Chip
                      key={subject}
                      label={subject}
                      color="primary"
                      variant={subject === 'Use of English' ? 'filled' : 'outlined'}
                    />
                ))}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/')}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={handleNext}
                disabled={selectedSubjects.length !== 4 || loading}
              >
                Next
              </Button>
            </Box>
          </CardContent>
        </Card>
        {/* SEB support removed — no external SEB prompts shown */}
      </Container>

    </Box>
  );
};

export default JambSubjectSelection;
